import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import axios from "axios";

const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  "Content-Type": "application/json",
};

export interface UserType {
  id?: number;
  usersType: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// 🔹 Fetch user types with filters
export const fetchUsersType = createAsyncThunk(
  "department/fetchUsersType",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) {
        params.search = search.trim() || "";
      }

      if (sort) {
        params.ordering = order === "desc" ? `-${sort}` : sort;
      }

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          const { id, value } = filter;
          if (!id || value === undefined || value === "") return;

          // 🔹 Handle date filters correctly
          if (id === "created_at" && value && typeof value === "object") {
            const dateFilter = value as { from_date?: string; to_date?: string };

            if (dateFilter.from_date) {
              params["created_at_date_from"] = dateFilter.from_date;
            }
            if (dateFilter.to_date) {
              params["created_at_date_to"] = dateFilter.to_date;
            }
            return;
          }

          if (id === "updated_at" && value && typeof value === "object") {
            const dateFilter = value as { from_date?: string; to_date?: string };

            if (dateFilter.from_date) {
              params["updated_at_date_from"] = dateFilter.from_date;
            }
            if (dateFilter.to_date) {
              params["updated_at_date_to"] = dateFilter.to_date;
            }
            return;
          }


          // Default filters
          params[id] = value;
        });
      }

      const response = await Axios.get(`api/usertype/`, { params });

      return {
        data: response?.data?.data || response?.data,
        rowCount: response?.data?.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchUsersType error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch user types");
    }
  }
);

// 🔹 Get by ID
export const fetchUsersTypesById = createAsyncThunk(
  "usersTypes/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`${API_URL}/organisation/${id}/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch organisations by ID"
      );
    }
  }
);

// 🔹 Update organisation
export const updateUsersTypes = createAsyncThunk(
  "usersTypes/updateOrganisation",
  async (orgData: any) => {
    const response: any = await Axios.put(
      `${API_URL}/organization/${orgData?.organization_id}/update/`,
      orgData
    );
    return response;
  }
);

export const updateStatusInUsersTypes = createAsyncThunk(
  "usersTypes/updateStatusInUsersTypes",
  async ({ user_type_id, is_active }: { user_type_id: string; is_active: boolean }) => {
    const response: any = await Axios.patch(
      `${API_URL}api/usertype/edit-status/${user_type_id}/`,
      { is_active } // send only status in payload
    );
    return response.data;
  }
);



export const addUsersTypes = createAsyncThunk(
  "usersTypes/addUsers",
  async (packageData: any) => {
    const response = await Axios.post(`${API_URL}/organisation/`, packageData);
    return response.data;
  }
);

// 🔹 Delete
export const deleteOrganisation = createAsyncThunk(
  "packages/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 🔹 Dropdowns
export const fetchAllDropdownsUsersTypes = createAsyncThunk(
  "usersTypes/fetchAllDropdownsUsersTypes",
  async () => {
    return [];
  }
);

//  Export CSV via thunk
export const exportetchUsersTypes = createAsyncThunk(
  "usersTypes/exportetchUsersTypes",
  async (params: { search?: string; filters?: any }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };

      const query = new URLSearchParams();
      query.append("export", "excel");

      if (params.search) {
        query.append("search", params.search);
      }

      if (params.filters && params.filters.length > 0) {
        params.filters.forEach(({ id, value }: { id: string; value: any }) => {
          if (!id || value === undefined || value === null) return;

          if (
            (id === "created_at" || id === "updated_at") &&
            typeof value === "object"
          ) {
            if (value.from_date) {
              query.append(`${id}_date_from`, value.from_date);
            }
            if (value.to_date) {
              query.append(`${id}_date_to`, value.to_date);
            }
          } else {
            query.append(id, String(value));
          }
        });
      }

      const response = await Axios.get(`/user-types/?${query.toString()}`, {
        headers,
        responseType: "blob",
      });

      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "usersTypes.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

// 🔹 Export CSV via direct fn
export const UsersTypesxportsCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();
  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(({ id, value }: { id: string; value: any }) => {
      if (!id || value === undefined || value === null) return;

      if (
        (id === "created_at" || id === "updated_at") &&
        typeof value === "object"
      ) {
        if (value.from_date) {
          params.append(`${id}_date_from`, value.from_date);
        }
        if (value.to_date) {
          params.append(`${id}_date_to`, value.to_date);
        }
      } else {
        params.append(id, String(value));
      }
    });
  }

  const response = await Axios.get(`/user-types/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Userstype.csv",
  };
};
